/**
 * Request a Quote - variant level purchase mode controller
 * ---------------------------------------------------------------------------
 * Source of truth: variant.metafields.custom.request_quote (boolean).
 *
 * The metafield is exposed to the DOM in two shapes:
 *   1. <script type="application/json" data-quote-json>{ ...product payload... }</script>
 *      Full payload (id/title/sku/quote per variant) - used by the PDP and by
 *      the Quick View lookup template (/products/<handle>?view=quote-data).
 *   2. [data-quote-variants="123,456"] - a compact list of quote-only variant
 *      ids, used by product cards and other light-weight grid surfaces.
 *
 * Every purchase surface (PDP section, Quick View modal, card form, sticky
 * cart) is a "scope": an element carrying [data-quote-scope]. Elements inside a
 * scope opt into the behaviour with [data-quote-hide] (hidden while the
 * selected variant is quote-only) or [data-quote-show] (shown only then).
 * Toggling walks up from each element to its NEAREST scope, so a PDP switching
 * into quote mode never touches prices or buttons inside nested product cards
 * (related products, recommendations, FBT rows).
 */
window.QuoteMode = (function () {
  'use strict';

  var SCOPE_ATTR = 'data-quote-scope';
  var HIDDEN_CLASS = 'quote-hidden';

  var quoteVariants = {}; // variantId -> true
  var variantIndex = {}; // variantId -> { id, title, sku, productTitle, productUrl, productHandle }
  var productRequests = {}; // handle -> Promise

  /* ----------------------------------------------------------- utilities */

  function key(id) {
    if (id === null || id === undefined) return '';
    return String(id).trim();
  }

  function toArray(nodeList) {
    return Array.prototype.slice.call(nodeList || []);
  }

  function rootUrl() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return '/';
  }

  /* ------------------------------------------------------------ registry */

  function isQuoteVariant(id) {
    var k = key(id);
    return !!k && quoteVariants[k] === true;
  }

  function variantInfo(id) {
    return variantIndex[key(id)] || null;
  }

  function registerIds(list) {
    if (!list) return;
    var ids = Array.isArray(list) ? list : String(list).split(',');
    for (var i = 0; i < ids.length; i++) {
      var k = key(ids[i]);
      if (k) quoteVariants[k] = true;
    }
  }

  function registerProduct(data) {
    if (!data || !data.variants || !data.variants.length) return data;

    for (var i = 0; i < data.variants.length; i++) {
      var variant = data.variants[i];
      var k = key(variant.id);
      if (!k) continue;

      if (variant.quote === true) {
        quoteVariants[k] = true;
      } else if (quoteVariants[k] !== true) {
        quoteVariants[k] = false;
      }

      variantIndex[k] = {
        id: variant.id,
        title: variant.title || '',
        sku: variant.sku || '',
        productTitle: data.title || '',
        productUrl: data.url || '',
        productHandle: data.handle || ''
      };
    }

    return data;
  }

  /**
   * Read every quote payload inside `context` and add it to the registry.
   */
  function scan(context) {
    var ctx = context || document;
    if (!ctx.querySelectorAll) return;

    toArray(ctx.querySelectorAll('script[data-quote-json]')).forEach(function (script) {
      if (script.getAttribute('data-quote-parsed') === 'true') return;
      script.setAttribute('data-quote-parsed', 'true');
      try {
        registerProduct(JSON.parse(script.textContent));
      } catch (error) {
        /* malformed payload - leave the surface purchasable */
      }
    });

    toArray(ctx.querySelectorAll('[data-quote-variants]')).forEach(function (element) {
      registerIds(element.getAttribute('data-quote-variants'));
    });
  }

  /**
   * Quick View and other AJAX surfaces have no Liquid context, so the variant
   * metafields are fetched from the dedicated alternate product template.
   */
  function fetchProductQuoteData(handle) {
    var slug = key(handle);
    if (!slug) return Promise.resolve(null);
    if (productRequests[slug]) return productRequests[slug];

    productRequests[slug] = fetch(
      rootUrl() + 'products/' + encodeURIComponent(slug) + '?view=quote-data',
      { credentials: 'same-origin', headers: { Accept: 'application/json' } }
    )
      .then(function (response) {
        if (!response.ok) throw new Error('Unable to load quote data.');
        return response.json();
      })
      .then(registerProduct)
      .catch(function () {
        return null;
      });

    return productRequests[slug];
  }

  /**
   * True when every variant of a product is quote-only, i.e. the product has no
   * purchasable variant at all. Used by the list surfaces (wishlist, compare,
   * recently viewed) that are built from /products/<handle>.js, which does not
   * carry metafields.
   */
  function productIsAllQuote(handle) {
    return fetchProductQuoteData(handle).then(function (data) {
      if (!data || !data.variants || !data.variants.length) return false;
      return data.variants.every(function (variant) {
        return variant.quote === true;
      });
    });
  }

  /* -------------------------------------------------------------- scopes */

  function closestScope(element) {
    if (!element || !element.closest) return null;
    return element.closest('[' + SCOPE_ATTR + ']');
  }

  /**
   * Elements inside `scope` whose NEAREST scope is `scope` itself. This is what
   * keeps a PDP in quote mode from hiding anything in nested product cards.
   */
  function ownElements(scope, selector) {
    return toArray(scope.querySelectorAll(selector)).filter(function (element) {
      var owner = element.parentElement ? closestScope(element.parentElement) : null;
      return owner === scope;
    });
  }

  function setHidden(element, hidden) {
    element.classList.toggle(HIDDEN_CLASS, !!hidden);
    if (hidden) {
      element.setAttribute('aria-hidden', 'true');
    } else {
      element.removeAttribute('aria-hidden');
    }
  }

  function detailsFor(scope, variant, variantId) {
    var known = variantInfo(variantId) || {};
    var scopeTitle = scope.getAttribute('data-product-title');
    var scopeUrl = scope.getAttribute('data-product-url');

    return {
      productTitle: scopeTitle || known.productTitle || '',
      productUrl: scopeUrl || known.productUrl || '',
      variantId: variantId || '',
      variantTitle: (variant && variant.title) || known.title || '',
      variantSku: (variant && variant.sku) || known.sku || ''
    };
  }

  function applyDetails(element, details) {
    element.setAttribute('data-product-title', details.productTitle);
    element.setAttribute('data-product-url', details.productUrl);
    element.setAttribute('data-variant-id', details.variantId);
    element.setAttribute('data-variant-title', details.variantTitle);
    element.setAttribute('data-variant-sku', details.variantSku);
  }

  /**
   * The single entry point every variant-change flow calls.
   *
   * @param {Element} scope   Purchase surface root ([data-quote-scope]).
   * @param {Object|Number} variant  Variant object or bare id. Pass null when no
   *   variant is selectable (invalid option combination) and undefined to
   *   re-evaluate the variant the scope already records.
   * @return {Boolean} whether the surface is now in quote mode.
   */
  function updatePurchaseMode(scope, variant) {
    if (!scope || scope.nodeType !== 1) return false;
    if (!scope.hasAttribute(SCOPE_ATTR)) scope.setAttribute(SCOPE_ATTR, '');

    var variantId;
    if (variant === undefined) {
      variantId = key(scope.getAttribute('data-quote-current-variant'));
    } else if (variant && typeof variant === 'object') {
      variantId = key(variant.id);
    } else {
      variantId = key(variant);
    }

    var quote = isQuoteVariant(variantId);

    scope.setAttribute('data-quote-active', quote ? 'true' : 'false');
    scope.classList.toggle('quote-mode', quote);
    if (variantId) {
      scope.setAttribute('data-quote-current-variant', variantId);
    } else if (variant !== undefined) {
      scope.removeAttribute('data-quote-current-variant');
    }

    ownElements(scope, '[data-quote-hide]').forEach(function (element) {
      setHidden(element, quote);
    });
    ownElements(scope, '[data-quote-show]').forEach(function (element) {
      setHidden(element, !quote);
    });

    var details = detailsFor(scope, typeof variant === 'object' ? variant : null, variantId);
    ownElements(scope, '[data-quote-trigger]').forEach(function (element) {
      applyDetails(element, details);
    });

    document.dispatchEvent(
      new CustomEvent('quote:modechange', {
        detail: { scope: scope, variantId: variantId, quote: quote, details: details }
      })
    );

    return quote;
  }

  /** Re-evaluate every scope in `context` from its recorded current variant. */
  function refresh(context) {
    var ctx = context || document;
    toArray(ctx.querySelectorAll('[' + SCOPE_ATTR + ']')).forEach(function (scope) {
      updatePurchaseMode(scope, undefined);
    });
  }

  /* -------------------------------------------------- quote modal bridge */

  function openQuote(details) {
    if (window.RequestQuoteModal && window.RequestQuoteModal.open) {
      window.RequestQuoteModal.open(details || {});
      return true;
    }
    return false;
  }

  function handleFromUrl(url) {
    if (!url) return '';
    var match = String(url).match(/\/products\/([^/?#]+)/);
    return match ? match[1] : '';
  }

  function detailsFromElement(element) {
    var scope = closestScope(element);
    var gridCartId = element.matches('.js-grid-cart') ? element.getAttribute('data-id') : '';
    var variantId =
      element.getAttribute('data-variant-id') ||
      gridCartId ||
      (scope && scope.getAttribute('data-quote-current-variant')) ||
      '';
    var known = variantInfo(variantId) || {};

    return {
      productTitle:
        element.getAttribute('data-product-title') ||
        (scope && scope.getAttribute('data-product-title')) ||
        known.productTitle ||
        '',
      productUrl:
        element.getAttribute('data-product-url') ||
        (scope && scope.getAttribute('data-product-url')) ||
        known.productUrl ||
        '',
      variantId: variantId,
      variantTitle: element.getAttribute('data-variant-title') || known.title || '',
      variantSku: element.getAttribute('data-variant-sku') || known.sku || ''
    };
  }

  /**
   * Opens the popup for the variant behind `element`, filling in the variant
   * title and SKU from the quote payload when the element itself does not carry
   * them (product cards only know the ids).
   */
  function openQuoteFor(element) {
    var details = detailsFromElement(element);
    var handle = handleFromUrl(details.productUrl);

    if (details.variantId && handle && (!details.variantTitle || !details.variantSku)) {
      fetchProductQuoteData(handle).then(function () {
        var known = variantInfo(details.variantId);
        if (known) {
          details.variantTitle = details.variantTitle || known.title;
          details.variantSku = details.variantSku || known.sku;
          details.productTitle = details.productTitle || known.productTitle;
          details.productUrl = details.productUrl || known.productUrl;
        }
        openQuote(details);
      });
      return;
    }

    openQuote(details);
  }

  /* ------------------------------------------------- purchase path guard */

  function blockedVariantFromForm(form) {
    var input = form.querySelector('[name="id"]:checked, select[name="id"], input[name="id"]');
    if (!input) return null;
    return isQuoteVariant(input.value) ? key(input.value) : null;
  }

  function stop(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  function guardSubmit(event) {
    var form = event.target;
    if (!form || form.nodeName !== 'FORM') return;
    if (!/\/cart\/add/.test(form.getAttribute('action') || '')) return;

    var blockedId = blockedVariantFromForm(form);
    if (!blockedId) return;

    stop(event);

    var scope = closestScope(form);
    var trigger = scope ? scope.querySelector('[data-quote-trigger]') : null;
    if (trigger) {
      openQuoteFor(trigger);
      return;
    }

    var known = variantInfo(blockedId) || {};
    openQuote({
      productTitle: known.productTitle || '',
      productUrl: known.productUrl || '',
      variantId: blockedId,
      variantTitle: known.title || '',
      variantSku: known.sku || ''
    });
  }

  function guardClick(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    // Quote triggers themselves.
    var trigger = target.closest('[data-quote-trigger], .js-request-quote');
    if (trigger) {
      stop(event);
      openQuoteFor(trigger);
      return;
    }

    // Grid "add to cart" buttons carry the variant id directly.
    var gridCart = target.closest('.js-grid-cart');
    if (gridCart && isQuoteVariant(gridCart.getAttribute('data-id'))) {
      stop(event);
      openQuoteFor(gridCart);
      return;
    }

    // Any button inside a scope that is currently in quote mode.
    var purchase = target.closest(
      'button[name="add"], .product-form__cart-submit, .qv-add-button, [data-fbt-add-button], .shopify-payment-button__button'
    );
    if (!purchase) return;
    if (purchase.hasAttribute('data-quote-trigger')) return;

    var scope = closestScope(purchase);
    if (scope && scope.getAttribute('data-quote-active') === 'true') {
      stop(event);
      var scopeTrigger = scope.querySelector('[data-quote-trigger]');
      if (scopeTrigger) {
        openQuoteFor(scopeTrigger);
      } else {
        openQuoteFor(purchase);
      }
    }
  }

  /**
   * Bundle / frequently-bought-together rows post several variants at once.
   * Quote-only variants are dropped from the payload rather than blocking the
   * whole basket, and fully quote-only rows are unchecked up front.
   */
  function pruneMultiAddRows() {
    toArray(document.querySelectorAll('.fbt-item[data-item]')).forEach(function (row) {
      if (!isQuoteVariant(row.getAttribute('data-item'))) return;
      row.setAttribute('data-checked', 'false');
      var checkbox = row.querySelector('.fbt-checkbox');
      if (checkbox) {
        checkbox.checked = false;
        checkbox.disabled = true;
      }
    });

    toArray(document.querySelectorAll('[data-related-checkbox], [data-main-checkbox]')).forEach(
      function (checkbox) {
        if (!isQuoteVariant(checkbox.getAttribute('data-variant-id'))) return;
        checkbox.checked = false;
        checkbox.disabled = true;
      }
    );
  }

  /* ---------------------------------------------------------------- init */

  function init() {
    scan(document);
    pruneMultiAddRows();
    refresh(document);
  }

  /**
   * Product grids arrive over AJAX in this theme (recommendations, complementary
   * products, infinite scroll, facets, Quick View). Watching for inserted quote
   * payloads keeps those surfaces registered without each of them having to
   * call scan() itself.
   */
  function observeInsertions() {
    if (!window.MutationObserver || !document.body) return;

    var pending = null;
    var observer = new MutationObserver(function (mutations) {
      var found = false;

      for (var i = 0; i < mutations.length && !found; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (
            node.matches('[data-quote-variants], script[data-quote-json], [' + SCOPE_ATTR + ']') ||
            node.querySelector('[data-quote-variants], script[data-quote-json], [' + SCOPE_ATTR + ']')
          ) {
            found = true;
            break;
          }
        }
      }

      if (!found) return;
      window.clearTimeout(pending);
      pending = window.setTimeout(function () {
        scan(document);
        pruneMultiAddRows();
        refresh(document);
      }, 100);
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  document.addEventListener('click', guardClick, true);
  document.addEventListener('submit', guardSubmit, true);
  document.addEventListener('change', function (event) {
    if (event.target && event.target.matches && event.target.matches('.fbtproduct-form__variants, [data-variant-select]')) {
      window.setTimeout(pruneMultiAddRows, 0);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
      observeInsertions();
    });
  } else {
    init();
    observeInsertions();
  }

  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
    refresh(event.target);
  });

  return {
    scan: scan,
    refresh: refresh,
    init: init,
    registerIds: registerIds,
    registerProduct: registerProduct,
    isQuoteVariant: isQuoteVariant,
    variantInfo: variantInfo,
    fetchProductQuoteData: fetchProductQuoteData,
    productIsAllQuote: productIsAllQuote,
    updatePurchaseMode: updatePurchaseMode,
    closestScope: closestScope,
    detailsFromElement: detailsFromElement,
    openQuoteFor: openQuoteFor,
    openQuote: openQuote
  };
})();
